import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { InspectionDetailDto, InspectionEntryService, InspectionPdfItemDto } from '@artemis-software/wr-api';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ServerSentEventsService } from '@services/event.service';
import { NbToastrService } from '@nebular/theme';

@Component({
  standalone: false,
  selector: 'wr-inspection-pdfs',
  templateUrl: './inspection-pdfs.component.html',
  styleUrls: ['./inspection-pdfs.component.scss'],
})
export class InspectionPdfsComponent {

  readonly context = inject(InspectionContext);
  private readonly inspectionEntryService = inject(InspectionEntryService);
  private readonly eventsService = inject(ServerSentEventsService);
  private readonly toastr = inject(NbToastrService);
  private readonly destroyRef = inject(DestroyRef);

  readonly inspectionPdfs = this.context.inspectionPdfs;
  readonly inspection = input.required<InspectionDetailDto>();
  readonly downloadingImages = signal(false);

  readonly pdfsByType = computed(() => {
    const inspectionPdfs = this.inspectionPdfs();
    return inspectionPdfs.reduce((map, inspectionPdf) => {
      map[inspectionPdf.inspectionPdfType] = inspectionPdf;
      return map;
    }, {} as Record<string, InspectionPdfItemDto>);
  });

  async downloadImages() {
    if (this.downloadingImages()) return;
    this.downloadingImages.set(true);

    const inspectionId = this.inspection().id;

    // Start listening before triggering the job so a fast completion event can't be missed.
    const eventPromise = firstValueFrom(
      this.eventsService.events('InspectionImageZipReady', 'InspectionImageZipFailed').pipe(
        filter(e => e.inspectionId === inspectionId),
        take(1),
        timeout(5 * 60 * 1000),
        takeUntilDestroyed(this.destroyRef),
      ),
    );
    // Avoid an unhandled rejection if the trigger call below fails and this promise is never awaited.
    eventPromise.catch(() => undefined);

    try {
      await firstValueFrom(this.inspectionEntryService.triggerImageDownload(inspectionId));
    } catch {
      this.downloadingImages.set(false);
      this.toastr.danger('Download konnte nicht gestartet werden.', 'Fehler');
      return;
    }

    try {
      const event = await eventPromise;
      if (event.type === 'InspectionImageZipReady') {
        const a = document.createElement('a');
        a.href = event.downloadUrl;
        a.download = 'begehung-bilder.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        if (event.skippedCount > 0) {
          this.toastr.warning(
            `${event.skippedCount} Bild(er) konnten nicht geladen werden und fehlen im ZIP.`,
            'Download unvollständig',
          );
        }
      } else if (event.reason === 'no_images') {
        this.toastr.warning('Keine Bilder gefunden.', 'Download');
      } else {
        this.toastr.danger('Export fehlgeschlagen. Bitte versuche es erneut.', 'Fehler');
      }
    } catch {
      this.toastr.danger('Download-Timeout. Bitte versuche es erneut.', 'Fehler');
    } finally {
      this.downloadingImages.set(false);
    }
  }
}
